import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ChevronLeft, ChevronRight, UserPlus } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Header from "../components/layout/Header";
import { studentService } from "../services/studentService";
import { canManageStaff } from "../hooks/useSchoolRole";
import InviteStaffModal from "../components/modals/InviteStaffModal";

// Staff roster page. Split out of ManageStudents (it used to render here via
// a `mode="staff"` prop) so the staff and student concerns can evolve
// independently — staff have roles, students have cohorts/import, and the
// shared `isStaffMode` branching was getting unwieldy. The genuinely shared
// chrome (search/tabs/pagination) is duplicated here for now; Stage 2 of the
// refactor extracts those into shared primitives.

const STAFF_TABS = ["Active Staff", "Pending Invites"];
const STAFF_TAB_LABELS = {
  "Active Staff": "manageStudents.tabs.activeStaff",
  "Pending Invites": "manageStudents.tabs.pendingInvites",
};

const STAFF_ROLE_OPTIONS = [
  { labelKey: "modals.inviteStaff.roles.owner", value: "OWNER" },
  { labelKey: "modals.inviteStaff.roles.billing", value: "BILLING" },
  { labelKey: "modals.inviteStaff.roles.admin", value: "ADMIN" },
  { labelKey: "modals.inviteStaff.roles.counselor", value: "COUNSELOR" },
  { labelKey: "modals.inviteStaff.roles.classroom_viewer", value: "CLASSROOM_VIEWER" },
];

const formatDate = (value, locale, fallback = "-") => {
  if (!value) return fallback;
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Teaching empty state for the staff roster — points a solo admin at the
// team-invite flow instead of a dead-end "no staff" line. Shows a neutral
// message when the table is empty only because a search matched nothing.
const StaffEmptyState = ({ filtered, onInvite }) => {
  const { t } = useTranslation();

  if (filtered) {
    return (
      <div className="px-6 py-16 flex flex-col items-center text-center gap-2">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-50 text-gray-400">
          <Search size={20} />
        </div>
        <p className="text-base font-semibold text-gray-900">
          {t("manageStudents.empty.noMatchStaff", { defaultValue: "No team members match your search" })}
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 py-16 flex flex-col items-center text-center gap-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
        <UserPlus size={22} />
      </div>
      <div>
        <h4 className="text-base font-bold text-gray-900">
          {t("manageStudents.empty.staffTitle", { defaultValue: "It's just you so far" })}
        </h4>
        <p className="text-sm text-gray-500 mt-1 max-w-md">
          {t("manageStudents.empty.staffHint", {
            defaultValue:
              "Invite counselors, teachers or co-admins so they can help manage students and issue certificates.",
          })}
        </p>
      </div>
      <button
        type="button"
        onClick={onInvite}
        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600 transition-colors"
      >
        <Plus size={15} />
        {t("manageStudents.inviteStaff", { defaultValue: "Invite Staff" })}
      </button>
    </div>
  );
};

const StaffMemberRow = ({ member, onUpdateRole, onUpdateStatus, isCurrentUser }) => {
  const { t, i18n } = useTranslation();
  const staffName = `${member.firstName || ""} ${member.lastName || ""}`.trim() || member.email;
  const isActive = member.status === "ACTIVE";
  const joinedOn = formatDate(member.joinedAt, i18n.resolvedLanguage || "en", "-");

  return (
    <tr className="h-16 hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4 text-gray-900 text-xs">
        <div className="inline-flex items-center gap-2">
          <span>{staffName}</span>
          {isCurrentUser ? (
            <span className="inline-flex items-center justify-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
              ({t("manageStudents.you")})
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-6 py-4 text-gray-900 text-xs">{member.email}</td>
      <td className="px-6 py-4 text-gray-900 text-xs">{member.title || "-"}</td>
      <td className="px-6 py-4">
        <select
          value={member.role}
          onChange={(e) => onUpdateRole(member, e.target.value)}
          className="px-2 py-1 text-xs font-medium rounded-md border border-blue-200 bg-white text-gray-900"
        >
          {STAFF_ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>
      </td>
      <td className="px-6 py-4">
        <span
          className={`px-2.5 py-0.5 rounded-[10px] text-xs font-medium ${
            isActive ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
          }`}
        >
          {isActive ? t("manageStudents.status.active") : t("manageStudents.status.inactive")}
        </span>
      </td>
      <td className="px-6 py-4 text-gray-900 text-xs">{joinedOn}</td>
      <td className="px-6 py-4">
        <button
          type="button"
          onClick={() => onUpdateStatus(member, isActive ? "INACTIVE" : "ACTIVE")}
          className={`px-2 py-2 text-xs font-medium rounded-md transition-colors ${
            isActive ? "bg-red-600 hover:bg-red-700 text-red-50" : "bg-emerald-600 hover:bg-emerald-700 text-white"
          }`}
        >
          {isActive ? t("manageStudents.actions.deactivate") : t("manageStudents.actions.activate")}
        </button>
      </td>
    </tr>
  );
};

const ManageStaff = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // The viewer's own rank. undefined = still loading (don't decide yet);
  // null = couldn't resolve. Used to keep sub-ADMIN users off this page even
  // if they hit the URL directly (the nav entry is already hidden for them).
  const [schoolRole, setSchoolRole] = useState(undefined);
  const [activeTab, setActiveTab] = useState("Active Staff");
  const [staffMembers, setStaffMembers] = useState([]);
  const [pendingStaffInvites, setPendingStaffInvites] = useState([]);
  const [showInviteStaffModal, setShowInviteStaffModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentUserId, setCurrentUserId] = useState(null);
  // Only the school/admin name is needed here (for the Header) — the rest of
  // the dashboard stats are student-centric and irrelevant to staff.
  const [schoolName, setSchoolName] = useState("");
  const [adminName, setAdminName] = useState("");

  // Staff management is ADMIN+ only (matches the backend gate). `allowed`
  // fails open on an unknown role so a missing/unresolved schoolRole never
  // locks an admin out; `roleResolved` lets us hold off the staff fetch (and
  // the redirect) until we actually know the rank. Declared up here so the
  // data-fetch effect below can read them.
  const roleResolved = schoolRole !== undefined;
  const allowed = canManageStaff(schoolRole);

  const fetchStaffMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await studentService.getSchoolMemberships();
      const allMembers = response || [];
      const query = searchQuery.trim().toLowerCase();
      const filtered = query
        ? allMembers.filter((member) => {
            const fullName = `${member.firstName || ""} ${member.lastName || ""}`.toLowerCase();
            return member.email.toLowerCase().includes(query) || fullName.includes(query) || (member.title || "").toLowerCase().includes(query);
          })
        : allMembers;

      const startIdx = (currentPage - 1) * itemsPerPage;
      const endIdx = startIdx + itemsPerPage;

      setStaffMembers(filtered.slice(startIdx, endIdx));
      setTotalCount(filtered.length);
      setTotalPages(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedFetchStaff"));
      console.error("Error fetching school staff:", error);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, currentPage, searchQuery, t]);

  const fetchPendingStaffInvites = useCallback(async () => {
    setLoading(true);
    try {
      const response = await studentService.getPendingStaffInvites();
      const allInvites = response || [];
      const query = searchQuery.trim().toLowerCase();
      const filtered = query
        ? allInvites.filter((invite) => {
            const fullName = `${invite.name || ""}`.toLowerCase();
            return (
              (invite.email || "").toLowerCase().includes(query) ||
              fullName.includes(query) ||
              (invite.title || "").toLowerCase().includes(query)
            );
          })
        : allInvites;

      const startIdx = (currentPage - 1) * itemsPerPage;
      const endIdx = startIdx + itemsPerPage;

      setPendingStaffInvites(filtered.slice(startIdx, endIdx));
      setTotalCount(filtered.length);
      setTotalPages(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedFetchPendingInvites", { defaultValue: "Failed to load pending invites" }));
      console.error("Error fetching pending staff invites:", error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentPage, itemsPerPage, t]);

  // Pull the school/admin name for the Header (same source the other pages use).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await studentService.getDashboardStats();
        if (cancelled) return;
        setSchoolName(response.schoolName || "");
        setAdminName([response.adminFirstName, response.adminLastName].filter(Boolean).join(" "));
        setSchoolRole(response.schoolRole ?? null);
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
        if (!cancelled) setSchoolRole(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const user = await studentService.getCurrentUserProfile();
        setCurrentUserId(user?.id || null);
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };

    fetchCurrentUser();
  }, []);

  // Refetch when tab / page / search changes. Wait until the rank is resolved
  // and skip entirely for confirmed sub-ADMIN viewers so we don't fire staff
  // endpoints that would 403 before the guard redirects them away.
  useEffect(() => {
    if (!roleResolved || !allowed) return;
    if (activeTab === "Pending Invites") {
      fetchPendingStaffInvites();
    } else {
      fetchStaffMembers();
    }
  }, [roleResolved, allowed, activeTab, fetchStaffMembers, fetchPendingStaffInvites]);

  const handleInviteStaff = async (payload) => {
    try {
      // Same as resend: the invite row is created either way, so a rejected
      // address comes back 200 with emailFailedAt set rather than throwing.
      // Report what actually happened — the row exists, the email didn't send.
      const created = await studentService.inviteSchoolStaff(payload);
      const invite = created?.invites?.[0];
      if (invite?.emailFailedAt) {
        toast.error(
          invite.emailError ||
            t("manageStudents.toasts.inviteUndeliverable", {
              defaultValue: "Couldn't deliver to that address. Check the spelling and try again.",
            }),
        );
      } else {
        toast.success(t("manageStudents.toasts.staffInviteSent"));
      }
      fetchStaffMembers();
      fetchPendingStaffInvites();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedInviteStaff"));
      console.error("Error inviting school staff:", error);
    }
    setShowInviteStaffModal(false);
  };

  const handleUpdateStaffRole = async (member, role) => {
    try {
      await studentService.updateSchoolMembershipRole(member.id, role);
      toast.success(t("manageStudents.toasts.staffRoleUpdated"));
      fetchStaffMembers();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedUpdateStaffRole"));
      console.error("Error updating staff role:", error);
    }
  };

  const handleUpdateStaffStatus = async (member, status) => {
    try {
      await studentService.updateSchoolMembershipStatus(member.id, status);
      toast.success(
        t(status === "ACTIVE" ? "manageStudents.toasts.staffActivated" : "manageStudents.toasts.staffDeactivated"),
      );
      fetchStaffMembers();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedUpdateStaffStatus"));
      console.error("Error updating staff status:", error);
    }
  };

  const handleResendStaffInvite = async (invite) => {
    try {
      // A rejected address no longer throws — the API records the failure on
      // the invite and returns 200 — so success has to be read off the
      // returned row, or a bounced resend would toast "Invite resent".
      const updated = await studentService.resendStaffInvite(invite.id);
      if (updated?.emailFailedAt) {
        toast.error(
          updated.emailError ||
            t("manageStudents.toasts.inviteUndeliverable", {
              defaultValue: "Couldn't deliver to that address. Check the spelling and try again.",
            }),
        );
      } else {
        toast.success(t("manageStudents.toasts.inviteResent", { defaultValue: "Invite resent" }));
      }
      fetchPendingStaffInvites();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedResendInvite", { defaultValue: "Failed to resend invite" }));
      console.error("Error resending staff invite:", error);
    }
  };

  const handleCancelStaffInvite = async (invite) => {
    if (!window.confirm(t("manageStudents.confirmCancelInvite", { defaultValue: `Cancel invite for ${invite.email}?` }))) {
      return;
    }
    try {
      await studentService.cancelStaffInvite(invite.id);
      toast.success(t("manageStudents.toasts.inviteCancelled", { defaultValue: "Invite cancelled" }));
      fetchPendingStaffInvites();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedCancelInvite", { defaultValue: "Failed to cancel invite" }));
      console.error("Error cancelling staff invite:", error);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    if (activeTab === "Pending Invites") {
      fetchPendingStaffInvites();
    } else {
      fetchStaffMembers();
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchQuery("");
  };

  // Direct-URL guard: staff management is ADMIN+ only. The nav entry is
  // already hidden for lower ranks, but bounce them to the dashboard if they
  // navigate here anyway. Only redirect once the rank is resolved AND
  // confirmed below ADMIN (the API would 403 every action regardless).
  useEffect(() => {
    if (roleResolved && !allowed) {
      navigate("/dashboard", { replace: true });
    }
  }, [roleResolved, allowed, navigate]);

  // Hold rendering until the rank is known so we never flash the staff
  // roster to someone who can't access it.
  if (!roleResolved) {
    return (
      <div className="bg-white min-h-screen font-manrope flex items-center justify-center">
        <div className="text-gray-500 text-lg">{t("manageStudents.loading")}</div>
      </div>
    );
  }
  if (!allowed) {
    return null;
  }

  return (
    <div className="bg-white min-h-screen font-manrope pb-[100px]">
      <Header schoolName={schoolName} adminName={adminName} />

      <div className="ml-[23px] mt-6 px-8 flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-0.5 font-['Manrope']">
            <h2 className="text-gray-900 text-2xl font-bold">{t("manageStudents.titleStaff")}</h2>
            <p className="text-gray-500 text-base font-normal">{t("manageStudents.subtitleStaff")}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowInviteStaffModal(true)}
              className="h-11 pl-3 pr-4 py-1.5 bg-primary hover:bg-blue-600 rounded-lg flex items-center gap-1.5 text-white font-medium transition-colors"
            >
              <Plus size={16} />
              {t("manageStudents.inviteStaff")}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-6 mt-6">
          <div className="border-b border-gray-200 flex gap-8">
            {STAFF_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => handleTabChange(tab)}
                className={`pb-4 text-lg font-semibold border-b-2 transition-all ${
                  activeTab === tab ? "border-primary text-primary" : "border-transparent text-gray-900 hover:text-gray-600"
                }`}
              >
                {t(STAFF_TAB_LABELS[tab], { defaultValue: tab })}
              </button>
            ))}
          </div>

          <div className="flex justify-between items-center gap-4">
            <div className="w-96 h-12 bg-white rounded-lg outline outline-1 outline-blue-100 flex overflow-hidden">
              <input
                type="text"
                placeholder={t("manageStudents.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="flex-1 px-4 py-3 text-gray-500 font-medium outline-none"
              />
              <button
                type="button"
                onClick={handleSearch}
                className="px-6 bg-secondary border-l border-blue-100 flex items-center gap-2 text-gray-500 font-medium hover:bg-blue-100 transition-colors"
              >
                <Search size={18} />
                {t("manageStudents.search")}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="bg-white rounded-xl outline outline-1 outline-blue-100 p-12 flex justify-center items-center">
              <div className="text-gray-500 text-lg">{t("manageStudents.loading")}</div>
            </div>
          ) : activeTab === "Pending Invites" ? (
            <div className="bg-white rounded-xl outline outline-1 outline-blue-100 overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-secondary">
                  <tr className="text-gray-900 text-xs font-semibold">
                    <th className="px-6 py-3 w-[20%]">{t("manageStudents.columns.name")}</th>
                    <th className="px-6 py-3 w-[26%]">{t("manageStudents.columns.email")}</th>
                    <th className="px-6 py-3 w-[14%]">{t("manageStudents.columns.role")}</th>
                    <th className="px-6 py-3 w-[12%]">{t("manageStudents.columns.status")}</th>
                    <th className="px-6 py-3 w-[14%]">{t("manageStudents.columns.invitedOn", { defaultValue: "Invited" })}</th>
                    <th className="px-6 py-3 w-[14%]">{t("manageStudents.columns.action")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {pendingStaffInvites.length > 0 ? (
                    pendingStaffInvites.map((invite) => {
                      const isExpired = invite.status === "EXPIRED";
                      // An invite the provider rejected also sits at PENDING with
                      // no emailSentAt, so without this it reads as a normal
                      // waiting-on-them row forever. Mirrors the students tab
                      // (ManageStudents PendingInviteRow).
                      const sendFailed = !isExpired && !invite.emailSentAt && !!invite.emailFailedAt;
                      return (
                        <tr key={invite.id} className="text-gray-900 text-sm">
                          <td className="px-6 py-4">{invite.name || "-"}</td>
                          <td className="px-6 py-4 break-all">{invite.email}</td>
                          <td className="px-6 py-4 capitalize">{(invite.schoolRole || "").toLowerCase().replace(/_/g, " ")}</td>
                          <td className="px-6 py-4">
                            <span
                              // Provider error on hover — it names the real
                              // problem ("invalid recipient", "domain not
                              // found"), which is what decides between fixing
                              // the address and dropping the row.
                              title={sendFailed ? invite.emailError || undefined : undefined}
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                sendFailed ? "bg-red-100 text-red-800" : isExpired ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"
                              }`}
                            >
                              {sendFailed
                                ? t("manageStudents.invites.invalidEmail", { defaultValue: "Invalid email" })
                                : isExpired
                                  ? t("manageStudents.status.expired", { defaultValue: "Expired" })
                                  : t("manageStudents.status.pending", { defaultValue: "Pending" })}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-500">{formatDate(invite.createdAt, i18n.language)}</td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleResendStaffInvite(invite)}
                                className="text-xs font-semibold text-primary hover:underline"
                              >
                                {t("manageStudents.actions.resend", { defaultValue: "Resend" })}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCancelStaffInvite(invite)}
                                className="text-xs font-semibold text-red-500 hover:underline"
                              >
                                {t("manageStudents.actions.cancel", { defaultValue: "Cancel" })}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        {t("manageStudents.empty.pendingStaffInvites", { defaultValue: "No pending staff invites." })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-xl outline outline-1 outline-blue-100 overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-secondary">
                  <tr className="text-gray-900 text-xs font-semibold">
                    <th className="px-6 py-3 w-[18%]">{t("manageStudents.columns.name")}</th>
                    <th className="px-6 py-3 w-[22%]">{t("manageStudents.columns.email")}</th>
                    <th className="px-6 py-3 w-[16%]">{t("manageStudents.columns.title")}</th>
                    <th className="px-6 py-3 w-[16%]">{t("manageStudents.columns.role")}</th>
                    <th className="px-6 py-3 w-[10%]">{t("manageStudents.columns.status")}</th>
                    <th className="px-6 py-3 w-[10%]">{t("manageStudents.columns.joined")}</th>
                    <th className="px-6 py-3 w-[8%]">{t("manageStudents.columns.action")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {staffMembers.length > 0 ? (
                    staffMembers.map((member) => (
                      <StaffMemberRow
                        key={member.id}
                        member={member}
                        isCurrentUser={currentUserId === member.userId}
                        onUpdateRole={handleUpdateStaffRole}
                        onUpdateStatus={handleUpdateStaffStatus}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="p-0">
                        <StaffEmptyState filtered={Boolean(searchQuery)} onInvite={() => setShowInviteStaffModal(true)} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-between items-center font-['Manrope']">
            <div className="flex items-center gap-2">
              <span className="text-gray-900 text-sm">{t("manageStudents.resultsPerPage")}</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="w-20 h-10 rounded-full outline outline-1 outline-blue-100 px-4 text-gray-500 text-sm cursor-pointer hover:bg-blue-50 transition-colors"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-2 bg-white rounded-full outline outline-1 outline-blue-100 p-1">
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="flex items-center">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-8 h-8 flex items-center justify-center text-sm font-medium rounded-full ${
                        currentPage === pageNum ? "bg-blue-100 text-gray-900" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <span className="text-gray-900 text-sm font-normal">
              {t("manageStudents.showingResults", {
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(currentPage * itemsPerPage, totalCount),
                total: totalCount,
              })}
            </span>
          </div>
        </div>
      </div>

      {showInviteStaffModal && <InviteStaffModal onClose={() => setShowInviteStaffModal(false)} onConfirm={handleInviteStaff} />}
    </div>
  );
};

export default ManageStaff;
